/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {
    BolInvoice,
    BolInvoiceItemType,
    CategoryType,
    VatDeclarationData,
    VatDeclarationInvalidation,
    VatPercentageData
} from '../util/interfaces';

import {deepClone} from '../util/utils';

import IDate from '@dvrd/idate';

interface Props extends BaseProps {
    period?: string;
    category?: BolInvoiceItemType;
    category_translation?: string;
    category_type?: CategoryType;
    invalidation?: VatDeclarationInvalidation | null;
    bol_invoices?: BolInvoice[];
    data?: VatDeclarationData;
    account_id?: string;
    merged_ids?: Array<string>;
    last_order_date?: string | null;
}

interface Fields extends BaseFields {
    period: string;
    category: BolInvoiceItemType;
    category_translation: string;
    category_type: CategoryType;
    invalidation: VatDeclarationInvalidation | null;
    bol_invoices: BolInvoice[];
    data: VatDeclarationData;
    account_id: string;
    merged_ids: Array<string>;
    last_order_date: string | null;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    period: '',
    category: BolInvoiceItemType.TURNOVER,
    category_translation: '',
    category_type: CategoryType.REVENUE,
    invalidation: null,
    bol_invoices: [],
    data: {},
    account_id: '',
    merged_ids: [],
    last_order_date: null
};

const fieldsDefinition: {[key in keyof Fields]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    period: createFieldDef(ModelFieldType.STRING),
    category: createFieldDef(ModelFieldType.STRING, BolInvoiceItemType.TURNOVER),
    category_translation: createFieldDef(ModelFieldType.STRING),
    category_type: createFieldDef(ModelFieldType.STRING, CategoryType.REVENUE),
    invalidation: createFieldDef(ModelFieldType.STRING, null, true),
    bol_invoices: createFieldDef(ModelFieldType.ARRAY),
    data: createFieldDef(ModelFieldType.OBJECT),
    account_id: createFieldDef(ModelFieldType.STRING),
    merged_ids: createFieldDef(ModelFieldType.ARRAY),
    last_order_date: createFieldDef(ModelFieldType.STRING, null, true)
};

const ignoreProps: Array<keyof Props> = [];

export default class VatDeclarationItem extends BaseModel<Props, Fields> {
    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get period(): string {
        return this.fields.period;
    }

    set period(period: string) {
        this.fields.period = period;
    }

    get category(): BolInvoiceItemType {
        return this.fields.category;
    }

    set category(category: BolInvoiceItemType) {
        this.fields.category = category;
    }

    get categoryTranslation(): string {
        return this.fields.category_translation;
    }

    get categoryType(): CategoryType {
        return this.fields.category_type;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get invalidation(): VatDeclarationInvalidation | null {
        return this.fields.invalidation;
    }

    get data(): VatDeclarationData {
        return this.fields.data;
    }

    set data(data: VatDeclarationData) {
        this.fields.data = data;
    }

    get bolInvoices(): BolInvoice[] {
        return this.fields.bol_invoices;
    }

    get mergedIds(): Array<string> {
        return this.fields.merged_ids;
    }

    set mergedIds(ids: Array<string>) {
        this.fields.merged_ids = ids;
    }

    get lastOrderDate(): IDate | null {
        if (!this.fields.last_order_date) return null;
        return new IDate(this.fields.last_order_date);
    }

    getTotalExclVat = (): number => {
        const {data} = this;
        let total: number = 0;
        for (const countryValues of Object.values(data)) {
            for (const vatValues of Object.values(countryValues)) {
                total += vatValues.total_incl_vat - vatValues.total_vat;
            }
        }
        return total;
    };

    getTotalVat = (): number => {
        const {data} = this;
        let total: number = 0;
        for (const countryValues of Object.values(data)) {
            for (const vatValues of Object.values(countryValues)) {
                total += vatValues.total_vat;
            }
        }
        return total;
    };

    getTotalInclVat = (): number => {
        const {data} = this;
        let total: number = 0;
        for (const countryValues of Object.values(data)) {
            for (const vatValues of Object.values(countryValues)) {
                total += vatValues.total_incl_vat + vatValues.total_incl_reversed_vat;
            }
        }
        return total;
    };

    getCountryCodes = (): Set<string> => {
        return new Set(Object.keys(this.data));
    };

    clone = (deep: boolean = true): VatDeclarationItem => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new VatDeclarationItem(fields);
    };

    merge = (item: VatDeclarationItem): VatDeclarationItem => {
        const newData = item.data;
        const currentData = this.data;
        this.mergedIds.push(item.id);
        if (item.lastOrderDate) {
            if (!this.lastOrderDate) this.fields.last_order_date = item.fields.last_order_date;
            else if (this.lastOrderDate.isBefore(item.lastOrderDate))
                this.fields.last_order_date = item.fields.last_order_date;
        }
        if (!Object.keys(currentData).length) {
            this.data = newData;
            return this;
        }
        for (const countryCode of Object.keys(newData)) {
            const newCountry = newData[countryCode];
            if (!Object.hasOwn(currentData, countryCode)) {
                currentData[countryCode] = newCountry;
                continue;
            }
            const currentCountry = currentData[countryCode];
            for (const vatPercentage of Object.keys(newCountry)) {
                const newVatValues: VatPercentageData = newCountry[vatPercentage];
                if (!Object.hasOwn(currentCountry, vatPercentage)) {
                    currentCountry[vatPercentage] = newVatValues;
                    continue;
                }
                const currentValues: VatPercentageData = currentCountry[vatPercentage];
                currentCountry[vatPercentage] = {
                    total_incl_vat: currentValues.total_incl_vat + newVatValues.total_incl_vat,
                    total_vat: currentValues.total_vat + newVatValues.total_vat,
                    total_incl_reversed_vat:
                        currentValues.total_incl_reversed_vat + newVatValues.total_incl_reversed_vat,
                    total_unknown_vat: currentValues.total_unknown_vat + newVatValues.total_unknown_vat
                };
            }
        }
        return this.clone();
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
    };
}
